import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi';

import { PLAN_DIM_MODAL_RADIUS } from 'pages/PlanRevamp/PlanPageUIRevamp/planDimSelectionModalStyles';
import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';

import colors from './dataTheme';

interface Properties {
  isOpen: boolean;
  /** Steps the user has already moved past, shown so they know what would be lost. */
  completedSteps: number;
  isEditing: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

/** Shown when the user tries to leave the action wizard after completing at least one step. */
const DiscardActionModal = ({
  isOpen,
  completedSteps,
  isEditing,
  onKeepEditing,
  onDiscard,
}: Properties) => (
  <Modal isOpen={isOpen} onClose={onKeepEditing} isCentered>
    <ModalOverlay bg='blackAlpha.400' />
    <ModalContent maxW='440px' borderRadius={PLAN_DIM_MODAL_RADIUS} overflow='hidden' mx={4}>
      <ModalBody px={6} pt={6} pb={2}>
        <Flex
          w='40px'
          h='40px'
          alignItems='center'
          justifyContent='center'
          borderRadius='full'
          bg={`${colors.warning}1A`}
          mb={4}
        >
          <FiAlertTriangle size={20} color={colors.warning} />
        </Flex>
        <Text {...PLAN_TEXT.modalTitle} color={colors.textDark}>
          {isEditing ? 'Discard your changes?' : 'Discard this action?'}
        </Text>
        <Text {...PLAN_TEXT.body} color={colors.textSecondary} mt={2}>
          {isEditing
            ? 'You have unsaved changes to this action. If you leave now, they will be lost and the action will stay as it was.'
            : `This action hasn't been saved yet. If you leave now, the ${
                completedSteps === 1 ? 'step' : `${completedSteps} steps`
              } you've completed and everything you've selected will be lost.`}
        </Text>
        <Box
          mt={4}
          px={3}
          py={2}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          bg={colors.headerBg}
          border='1px solid'
          borderColor={colors.border}
        >
          <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary}>
            You can&apos;t undo this. To finish later, keep editing and complete the remaining
            steps.
          </Text>
        </Box>
      </ModalBody>

      <ModalFooter px={6} py={5}>
        <Flex w='100%' gap={3}>
          <Button
            flex='1'
            h='40px'
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            variant='outline'
            borderColor={colors.border}
            bg={colors.surface}
            {...PLAN_TEXT.button}
            fontSize='14px'
            color={colors.textDark}
            onClick={onKeepEditing}
          >
            Keep editing
          </Button>
          <Button
            flex='1'
            h='40px'
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            bg={colors.danger}
            color={colors.white}
            {...PLAN_TEXT.button}
            fontSize='14px'
            _hover={{ bg: colors.danger, opacity: 0.9 }}
            onClick={onDiscard}
          >
            Discard &amp; exit
          </Button>
        </Flex>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

export default DiscardActionModal;
